import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { getLessonNote, saveLessonNote } from '../../lib/notes';
import { Stamp } from '../lms/ui';
import { Skeleton } from '../lms/Skeletons';

interface LessonNotesProps {
  userId: string;
  lessonId: string;
}

const LessonNotes: React.FC<LessonNotesProps> = ({ userId, lessonId }) => {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSaved(false);
    setError('');
    void getLessonNote(userId, lessonId).then((note) => {
      if (!cancelled) {
        setValue(note);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [userId, lessonId]);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await saveLessonNote(userId, lessonId, value);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your note.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-semibold text-ink">My notes</h2>
          <p className="mt-0.5 text-[12px] text-ink-2">Capture ideas, questions and action steps for your own export work.</p>
        </div>
        <Stamp tone="neutral">Private</Stamp>
      </div>

      {loading ? (
        <div className="mt-4 space-y-3 rounded-lg border border-rule bg-white p-3.5" role="status" aria-busy="true" aria-label="Loading notes">
          <span className="sr-only">Loading notes…</span>
          <Skeleton className="h-3.5 w-11/12 rounded-md" />
          <Skeleton className="h-3.5 w-full rounded-md" />
          <Skeleton className="h-3.5 w-4/5 rounded-md" />
          <Skeleton className="h-3.5 w-2/3 rounded-md" />
        </div>
      ) : (
        <>
          <textarea
            value={value}
            onChange={(e) => { setValue(e.target.value); setSaved(false); }}
            rows={7}
            placeholder="What matters from this lesson?"
            className="lms-field mt-4 resize-y leading-6"
          />
          {error && <p className="mt-1.5 text-[12px] text-danger">{error}</p>}
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="tnum font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-2">{value.length} characters</span>
            <button
              onClick={() => void save()}
              disabled={busy}
              className="lms-btn-primary !py-2 !text-[12px]"
            >
              {saved && <Check className="h-3.5 w-3.5" strokeWidth={2} />}
              {busy ? 'Saving…' : saved ? 'Saved' : 'Save note'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LessonNotes;
